import React, { useCallback } from 'react';
import {
  SingleDropdown,
  useSingleDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { useQueryClient } from '@tanstack/react-query';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.filter_row.advanced_filters');

type Props = {
  filter: AdvancedFilter;
  onSelectedItemChange: (e: { selectedItem?: Item<string> | null }) => void;
};

const Wrapper = styled(Grid)`
  min-width: 0;

  .MuiFormControl-fullWidth {
    width: 100%;
  }
`;

const SingleSelectFilter = ({ filter, onSelectedItemChange }: Props) => {
  const queryClient = useQueryClient();

  const fetchItems = useCallback(
    async (term: string) => {
      if (!filter.type) {
        return { items: [] };
      }

      if (filter.type.values) {
        const normalizedTerm = term.toLowerCase();
        const items = filter.type.values.filter((item) => {
          if (!normalizedTerm) {
            return true;
          }

          return item.label.toLowerCase().includes(normalizedTerm);
        });

        return { items };
      }

      const { value, valuesPath } = filter.type;

      if (!valuesPath) {
        return { items: [] };
      }

      const queryKey = ['filter', value, term];

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const response = await standardRequestWithErrorFlash.get(valuesPath, {
            params: {
              term: term,
              q: term,
            },
          });

          return response.data.results;
        },
        staleTime: Infinity,
      });

      return { items };
    },
    [filter.type, queryClient]
  );

  const singleDropdownProps = useSingleDropdown({
    onSelectedItemChange,
    selectedItem: filter.value as Item,
    fetchItems,
  });

  return (
    <Wrapper>
      <SingleDropdown dropdownProps={singleDropdownProps}>
        <SingleDropdown.Field
          placeholder={t('select')}
          ariaLabel={t('value')}
        />
        <SingleDropdown.Flyout />
      </SingleDropdown>
    </Wrapper>
  );
};

export default SingleSelectFilter;
